"use client";

import { Chart, useChart } from "@chakra-ui/charts";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TooltipProps } from "recharts";
import type {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import { getLabelGivenScoreValue } from "@/shared/config/backend";
import {
  Box,
  Button,
  Center,
  VStack,
  Text,
  Skeleton,
  Flex,
  SkeletonText,
  Heading,
} from "@chakra-ui/react";

const DEFAULT_CHART_HEIGHT = "52";
const DEFAULT_EMPTY_MESSAGE = "No data available";
const DEFAULT_BAR_CHART_COLOR = "blue.solid";
const REFLECT_ACTION_LABEL = "Reflect";
const DUMMY_BAR_HEIGHTS = ["56px", "88px", "72px", "104px", "68px"] as const;

export type GenericBarChartSkeletonProps = {
  emptyMessage?: string;
  height?: string;
  onAction?: () => void;
};

export function GenericBarChartSkeleton({
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  height = DEFAULT_CHART_HEIGHT,
  onAction,
}: GenericBarChartSkeletonProps) {
  const dummyBars = DUMMY_BAR_HEIGHTS.map((barHeight, index) => (
    <Flex key={index} direction="column" alignItems="center" flex="1">
      <Skeleton h={barHeight} width="4/5" borderRadius="md" />
      <SkeletonText mt="2" noOfLines={1} width="4/5" />
    </Flex>
  ));

  return (
    <Box h={height} position="relative" width="full">
      <Flex height="4/5" alignItems="flex-end" justifyContent="space-around">
        {dummyBars}
      </Flex>

      <Box position="absolute" pointerEvents="none" />

      <Center position="absolute" inset="0">
        <VStack>
          <Text fontStyle="italic">{emptyMessage}</Text>
          {onAction && (
            <Button onClick={onAction} bg="moveon.primary">
              {REFLECT_ACTION_LABEL}
            </Button>
          )}
        </VStack>
      </Center>
    </Box>
  );
}

function BarChartTooltipContent({
  active,
  payload,
  label,
}: TooltipProps<ValueType, NameType> & {
  label?: string | number;
  payload?: Array<{ value?: number | string }>;
}) {
  const numericLabel =
    typeof label === "number"
      ? label + 1
      : Number.isFinite(Number(label))
        ? Number(label) + 1
        : null;
  const wording =
    numericLabel === null
      ? String(label ?? "")
      : getLabelGivenScoreValue(numericLabel.toString());

  if (!active || !payload || !payload.length) return null;

  const rawValue = payload[0]?.value;
  const numericValue =
    typeof rawValue === "number"
      ? rawValue
      : typeof rawValue === "string"
        ? Number(rawValue)
        : 0;

  return (
    <Box>
      <Text>{wording}</Text>
      {numericValue > 0 ? (
        <Text>{`Count: ${numericValue}`}</Text>
      ) : (
        <Text>No responses</Text>
      )}
    </Box>
  );
}
export type BarChartColor = "blue.solid" | "purple.solid" | "green.solid";

export type BarChartFrequencyData = {
  count: number;
  answer: string;
};

export type GenericBarChartProps = {
  frequencyData?: BarChartFrequencyData[];
  chartColor?: BarChartColor;
  showBars?: boolean;
  showArea?: boolean;
  height?: string;
  onAction?: () => void;
  onActionMessage?: string;
  title?: string;
};

export default function GenericBarChart({
  frequencyData = [],
  chartColor = DEFAULT_BAR_CHART_COLOR,
  showBars = true,
  showArea = true,
  height = DEFAULT_CHART_HEIGHT,
  onAction,
  onActionMessage,
  title,
}: GenericBarChartProps) {
  const chart = useChart({
    data: frequencyData,
    series: [{ name: "count", color: chartColor }],
  });

  return (
    <Box position="relative" minW="0" w="full">
      {title && (
        <Heading size="sm" mb="4">
          {title}
        </Heading>
      )}

      <Chart.Root chart={chart} h={height} minH={height} minW="0" w="full">
        <ResponsiveContainer
          height="100%"
          initialDimension={{ width: 320, height: 208 }}
          minHeight={0}
          minWidth={0}
          width="100%"
        >
          <ComposedChart data={chart.data}>
            <CartesianGrid
              stroke={chart.color("border")}
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              axisLine={false}
              dataKey={chart.key("answer")}
              tickLine={false}
            />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} />
            <Tooltip content={<BarChartTooltipContent />} />

            {showBars &&
              chart.series.map((item) => (
                <Bar
                  isAnimationActive={false}
                  key={item.name}
                  dataKey={chart.key(item.name)}
                  fill={chart.color(item.color)}
                  opacity={0.5}
                  stroke={chart.color("bg")}
                />
              ))}
            {showArea && (
              <Area
                type="monotone"
                dataKey={chart.key("count")}
                fill={chart.color(chartColor)}
                stroke={chart.color(chartColor)}
                fillOpacity={0.1}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Chart.Root>

      {/* Absolutely positioned button overlay */}
      {onAction && (
        <Center position="absolute" inset="0" pointerEvents="none">
          <VStack>
            <Text fontStyle="italic">{onActionMessage}</Text>
            <Button
              onClick={onAction}
              bg="moveon.primary"
              color="white"
              pointerEvents="auto"
              boxShadow="sm"
            >
              {REFLECT_ACTION_LABEL}
            </Button>
          </VStack>
        </Center>
      )}
    </Box>
  );
}
